import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  Trophy, Calendar, Clock, Users, ArrowLeft, ChevronRight,
  Play, Timer, Award, Zap, CheckCircle, AlertCircle, Code,
  Brain, Rocket, Shield, BarChart3, Briefcase, Crown, Medal,
  Star, ExternalLink, Copy, Loader
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { loginUrlWithRedirect } from '../utils/urlConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contestTypeIcons = {
  coding: Code,
  ai_challenge: Brain,
  hackathon: Rocket,
  cybersecurity: Shield,
  data_science: BarChart3,
  hiring_challenge: Briefcase
};

const contestTypeColors = {
  coding: '#0a66c2',
  ai_challenge: '#7c3aed',
  hackathon: '#915907',
  cybersecurity: '#b24020',
  data_science: '#057642',
  hiring_challenge: '#0a66c2'
};

const CodeArenaContestsPage = () => {
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [contestTypes, setContestTypes] = useState({});

  useEffect(() => {
    fetchContests();
    fetchContestTypes();
  }, [activeTab]);

  const fetchContests = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/codearena/contests?status=${activeTab}`);
      setContests(res.data.contests || []);
    } catch (err) {
      console.error('Failed to fetch contests:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchContestTypes = async () => {
    try {
      const res = await axios.get(`${API}/codearena/contest-types`);
      setContestTypes(res.data.types || {});
    } catch (err) {
      console.error('Failed to fetch contest types:', err);
    }
  };

  const getTimeUntil = (dateString) => {
    const now = new Date();
    const target = new Date(dateString);
    const diff = target - now;
    
    if (diff < 0) return 'Started';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const mins = Math.floor((diff / (1000 * 60)) % 60);
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const getContestIcon = (type) => {
    return contestTypeIcons[type] || Code;
  };

  const getContestColor = (type) => {
    return contestTypeColors[type] || '#0a66c2';
  };

  return (
    <div className="min-h-screen bg-[#f3f2ef]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4 mb-3">
            <Link 
              to="/codearena" 
              className="flex items-center gap-2 text-[#666666] hover:text-[#191919] transition-colors text-sm"
              data-testid="back-to-codearena"
            >
              <ArrowLeft size={16} />
              Back to EvalentX Arena
            </Link>
          </div>
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-[#191919] flex items-center gap-2">
                <Trophy className="text-[#915907]" size={28} />
                Coding Contests
              </h1>
              <p className="text-[#666666] text-sm mt-0.5">Compete with developers nationwide and earn certificates</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-4">
            {[
              { id: 'upcoming', label: 'Upcoming', icon: Calendar },
              { id: 'live', label: 'Live Now', icon: Zap },
              { id: 'ended', label: 'Past Contests', icon: Award }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full font-medium transition-all text-sm ${
                    activeTab === tab.id 
                      ? 'bg-[#0a66c2] text-white' 
                      : 'bg-white text-[#666666] hover:bg-gray-50 border border-gray-200'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon size={14} />
                  {tab.label}
                  {tab.id === 'live' && contests.filter(c => c.status === 'live').length > 0 && (
                    <span className="w-2 h-2 bg-[#057642] rounded-full animate-pulse"></span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-5 animate-pulse shadow-sm">
                <div className="h-12 w-12 bg-gray-200 rounded-xl mb-4"></div>
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : contests.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {contests.map((contest, idx) => {
              const Icon = getContestIcon(contest.contest_type);
              const color = getContestColor(contest.contest_type);
              const isLive = contest.status === 'live';
              
              return (
                <div
                  key={idx}
                  className={`bg-white border rounded-xl p-5 transition-all hover:shadow-md shadow-sm ${
                    isLive 
                      ? 'border-[#057642]/50' 
                      : 'border-gray-200 hover:border-[#0a66c2]/30'
                  }`}
                  data-testid={`contest-card-${idx}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${color}15` }}
                    >
                      <Icon size={24} style={{ color }} />
                    </div>
                    <div className="flex items-center gap-1.5">
                      {isLive && (
                        <span className="flex items-center gap-1 bg-[#057642]/10 text-[#057642] px-2.5 py-0.5 rounded-full text-xs font-semibold border border-[#057642]/30 animate-pulse">
                          <span className="w-1.5 h-1.5 bg-[#057642] rounded-full"></span>
                          LIVE
                        </span>
                      )}
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                        contest.is_rated 
                          ? 'bg-[#0a66c2]/10 text-[#0a66c2] border-[#0a66c2]/30' 
                          : 'bg-gray-100 text-[#666666] border-gray-200'
                      }`}>
                        {contest.is_rated ? 'Rated' : 'Practice'}
                      </span>
                    </div>
                  </div>

                  <h3 className="text-lg font-semibold text-[#191919] mb-1.5">{contest.title}</h3>
                  <p className="text-[#666666] text-sm mb-3 line-clamp-2">{contest.description}</p>

                  {/* Prizes */}
                  {contest.prizes && contest.prizes.first && (
                    <div className="flex items-center gap-1.5 mb-3 bg-[#915907]/10 border border-[#915907]/20 rounded-lg px-3 py-1.5">
                      <Trophy className="text-[#915907]" size={14} />
                      <span className="text-[#915907] text-sm font-semibold">
                        1st: {contest.prizes.first}
                      </span>
                    </div>
                  )}

                  {/* Contest Info */}
                  <div className="space-y-1.5 mb-4">
                    <div className="flex items-center gap-1.5 text-sm text-[#666666]">
                      <Calendar size={12} />
                      <span>{new Date(contest.start_time).toLocaleDateString('en-US', { 
                        weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-[#666666]">
                      <Clock size={12} />
                      <span>{contest.duration_minutes} minutes</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-[#666666]">
                      <Users size={12} />
                      <span>{contest.participant_count || 0} registered</span>
                    </div>
                  </div>

                  {/* Domains */}
                  {contest.domains && contest.domains.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {contest.domains.slice(0, 2).map((domain, didx) => (
                        <span key={didx} className="text-xs px-2 py-0.5 rounded-full" style={{ 
                          backgroundColor: `${color}10`, 
                          color: color,
                          border: `1px solid ${color}30`
                        }}>
                          {domain}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Time/Status Badge */}
                  {contest.status === 'upcoming' && (
                    <div className="flex items-center justify-between mb-3 bg-[#f3f2ef] rounded-lg px-3 py-2">
                      <span className="text-sm text-[#666666]">Starts in</span>
                      <span className="text-base font-bold text-[#0a66c2]">{getTimeUntil(contest.start_time)}</span>
                    </div>
                  )}

                  <Link
                    to={`/codearena/contest/${contest.id}`}
                    className={`w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full font-medium transition-all text-sm ${
                      isLive
                        ? 'bg-[#057642] text-white hover:bg-[#046638]'
                        : 'bg-[#f3f2ef] text-[#191919] border border-gray-200 hover:bg-gray-100'
                    }`}
                    data-testid={`contest-link-${idx}`}
                  >
                    {isLive ? (
                      <>
                        <Play size={14} />
                        Enter Contest
                      </>
                    ) : contest.status === 'upcoming' ? (
                      <>
                        View Details
                        <ChevronRight size={14} />
                      </>
                    ) : (
                      <>
                        View Results
                        <ChevronRight size={14} />
                      </>
                    )}
                  </Link>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center shadow-sm">
            <Trophy className="mx-auto text-gray-300 mb-4" size={48} />
            <h3 className="font-semibold text-[#191919] mb-2">
              {activeTab === 'upcoming' ? 'No Upcoming Contests' : 
               activeTab === 'live' ? 'No Live Contests' : 'No Past Contests'}
            </h3>
            <p className="text-[#666666] text-sm">
              {activeTab === 'upcoming' 
                ? 'New contests will be announced soon!' 
                : 'Check the upcoming tab for future contests.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Contest Detail Page
export const CodeArenaContestDetailPage = () => {
  const { contestId } = useParams();
  const navigate = useNavigate();
  const [contest, setContest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [myStatus, setMyStatus] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  const isLoggedIn = !!localStorage.getItem('codearena_token');
  const token = localStorage.getItem('codearena_token');

  useEffect(() => {
    fetchContest();
    if (isLoggedIn) {
      fetchMyStatus();
    }
    fetchLeaderboard();
  }, [contestId]);

  const fetchContest = async () => {
    try {
      const res = await axios.get(`${API}/codearena/contests/${contestId}`);
      setContest(res.data);
    } catch (err) {
      console.error('Failed to fetch contest:', err);
      toast.error('Contest not found');
      navigate('/codearena/contests');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyStatus = async () => {
    try {
      const res = await axios.get(`${API}/codearena/contests/${contestId}/my-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch status:', err);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/codearena/contests/${contestId}/leaderboard?limit=50`);
      setLeaderboard(res.data.leaderboard || []);
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    }
  };

  const handleRegister = async () => {
    if (!isLoggedIn) {
      toast.error('Please login to register');
      navigate(loginUrlWithRedirect('/codearena/auth'));
      return;
    }

    setRegistering(true);
    try {
      await axios.post(
        `${API}/codearena/contests/${contestId}/register`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Successfully registered for contest!');
      fetchMyStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setRegistering(false);
    }
  };

  const copyContestLink = () => {
    navigator.clipboard.writeText(`https://evalentx.com/codearena/contest/${contestId}`);
    toast.success('Contest link copied!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f3f2ef] flex items-center justify-center">
        <Loader className="animate-spin text-[#0a66c2]" size={40} />
      </div>
    );
  }

  if (!contest) return null;

  const Icon = contestTypeIcons[contest.contest_type] || Code;
  const color = contestTypeColors[contest.contest_type] || '#0a66c2';
  const isLive = contest.status === 'live';
  const isEnded = contest.status === 'ended';
  const isUpcoming = contest.status === 'upcoming';
  const hasAssessment = contest.is_hiring_challenge && contest.assessment_id;

  // Handler for starting assessment (for hiring challenges)
  const handleStartAssessment = () => {
    if (!isLoggedIn) {
      toast.error('Please login to start the assessment');
      navigate(loginUrlWithRedirect('/codearena/auth'));
      return;
    }
    // Navigate to assessment page with access code
    const accessCode = contest.assessment_access_code || '';
    navigate(`/assessment/${contest.assessment_id}/register?code=${accessCode}`);
  };

  return (
    <div className="min-h-screen bg-[#f3f2ef]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <Link 
            to="/codearena/contests" 
            className="flex items-center gap-2 text-[#666666] hover:text-[#191919] transition-colors mb-3 text-sm"
          >
            <ArrowLeft size={16} />
            Back to Contests
          </Link>
          
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2.5 mb-2">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${color}15` }}
                >
                  <Icon size={20} style={{ color }} />
                </div>
                <div className="flex items-center gap-1.5">
                  {isLive && (
                    <span className="flex items-center gap-1 bg-[#057642]/10 text-[#057642] px-2.5 py-0.5 rounded-full text-xs font-semibold border border-[#057642]/30 animate-pulse">
                      <span className="w-1.5 h-1.5 bg-[#057642] rounded-full"></span>
                      LIVE NOW
                    </span>
                  )}
                  {isEnded && (
                    <span className="px-2.5 py-0.5 bg-gray-100 text-[#666666] rounded-full text-xs font-semibold border border-gray-200">
                      Ended
                    </span>
                  )}
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                    contest.is_rated 
                      ? 'bg-[#0a66c2]/10 text-[#0a66c2] border-[#0a66c2]/30' 
                      : 'bg-gray-100 text-[#666666] border-gray-200'
                  }`}>
                    {contest.is_rated ? 'Rated Contest' : 'Practice Contest'}
                  </span>
                </div>
              </div>
              <h1 className="text-2xl font-semibold text-[#191919] mb-1">{contest.title}</h1>
              <p className="text-[#666666] text-sm">{contest.description}</p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={copyContestLink}
                className="p-2.5 bg-[#f3f2ef] rounded-lg hover:bg-gray-200 transition-colors border border-gray-200"
                title="Copy Link"
              >
                <Copy size={16} className="text-[#666666]" />
              </button>
              <ShareButton kind="contest" id={contestId} label="Share" variant="outline" size="md" testid="share-contest-btn" />
              
              {isUpcoming && !myStatus?.is_registered && (
                <button
                  onClick={handleRegister}
                  disabled={registering}
                  className="flex items-center gap-1.5 px-5 py-2.5 rounded-full font-medium transition-all bg-[#0a66c2] text-white hover:bg-[#004182] text-sm"
                  data-testid="register-btn"
                >
                  {registering ? 'Registering...' : (
                    <>
                      <Play size={16} />
                      Register Now
                    </>
                  )}
                </button>
              )}
              
              {myStatus?.is_registered && isUpcoming && (
                <div className="flex items-center gap-1.5 px-5 py-2.5 bg-[#057642]/10 text-[#057642] rounded-full border border-[#057642]/30 text-sm font-medium">
                  <CheckCircle size={16} />
                  Registered
                </div>
              )}
              
              {isLive && myStatus?.is_registered && !hasAssessment && (
                <button
                  onClick={() => setActiveTab('problems')}
                  className="flex items-center gap-1.5 px-5 py-2.5 rounded-full font-medium transition-all bg-[#057642] text-white hover:bg-[#046638] text-sm"
                >
                  <Play size={16} />
                  Start Solving
                </button>
              )}

              {/* Start Assessment button for hiring challenges with linked assessment */}
              {isLive && hasAssessment && (
                <button
                  onClick={handleStartAssessment}
                  className="flex items-center gap-1.5 px-5 py-2.5 rounded-full font-medium transition-all bg-[#057642] text-white hover:bg-[#046638] text-sm"
                  data-testid="start-assessment-btn"
                >
                  <Play size={16} />
                  Start Assessment
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-4">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'problems', label: hasAssessment ? 'Assessment' : 'Problems', disabled: (isUpcoming && !isEnded) || hasAssessment },
              { id: 'leaderboard', label: 'Leaderboard' },
              ...(myStatus?.is_registered ? [{ id: 'my-submissions', label: 'My Submissions' }] : [])
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => !tab.disabled && setActiveTab(tab.id)}
                disabled={tab.disabled}
                className={`px-4 py-2 rounded-full font-medium transition-all text-sm ${
                  activeTab === tab.id 
                    ? 'bg-[#f3f2ef] text-[#191919]' 
                    : tab.disabled 
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-[#666666] hover:text-[#191919] hover:bg-gray-50'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
                {tab.id === 'problems' && isUpcoming && (
                  <span className="ml-1 text-xs text-[#666666]">(Locked)</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              {/* Assessment Notice for Hiring Challenges */}
              {hasAssessment && isLive && (
                <div className="bg-[#0a66c2]/10 border border-[#0a66c2]/30 rounded-xl p-5 shadow-sm">
                  <h3 className="text-lg font-semibold text-[#0a66c2] mb-2 flex items-center gap-2">
                    <Briefcase size={20} />
                    Hiring Challenge Assessment
                  </h3>
                  <p className="text-[#666666] text-sm mb-4">
                    This hiring challenge includes a technical assessment. Click the button below to start your assessment.
                  </p>
                  <button
                    onClick={handleStartAssessment}
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#0a66c2] text-white rounded-full font-medium hover:bg-[#004182] transition-colors text-sm"
                    data-testid="start-assessment-overview-btn"
                  >
                    <Play size={16} />
                    Start Assessment Now
                  </button>
                </div>
              )}

              {/* Prizes */}
              {contest.prizes && Object.keys(contest.prizes).length > 0 && (
                <div className="bg-white border border-[#915907]/30 rounded-xl p-5 shadow-sm">
                  <h3 className="text-lg font-semibold text-[#191919] mb-4 flex items-center gap-2">
                    <Trophy className="text-[#915907]" size={20} />
                    Prize Pool
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    {contest.prizes.first && (
                      <div className="text-center">
                        <div className="w-14 h-14 bg-gradient-to-br from-[#915907] to-[#b87708] rounded-full mx-auto mb-2 flex items-center justify-center shadow-lg">
                          <Crown className="text-white" size={24} />
                        </div>
                        <div className="text-xl font-bold text-[#915907]">{contest.prizes.first}</div>
                        <div className="text-sm text-[#666666]">1st Place</div>
                      </div>
                    )}
                    {contest.prizes.second && (
                      <div className="text-center">
                        <div className="w-12 h-12 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full mx-auto mb-2 flex items-center justify-center">
                          <Medal className="text-white" size={20} />
                        </div>
                        <div className="text-lg font-bold text-gray-500">{contest.prizes.second}</div>
                        <div className="text-sm text-[#666666]">2nd Place</div>
                      </div>
                    )}
                    {contest.prizes.third && (
                      <div className="text-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#b24020] to-[#d35030] rounded-full mx-auto mb-2 flex items-center justify-center">
                          <Medal className="text-white" size={16} />
                        </div>
                        <div className="text-base font-bold text-[#b24020]">{contest.prizes.third}</div>
                        <div className="text-sm text-[#666666]">3rd Place</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Rules */}
              {contest.rules && contest.rules.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                  <h3 className="text-lg font-semibold text-[#191919] mb-3">Contest Rules</h3>
                  <ul className="space-y-2">
                    {contest.rules.map((rule, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-[#666666] text-sm">
                        <CheckCircle className="text-[#057642] flex-shrink-0 mt-0.5" size={14} />
                        <span>{rule}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Eligibility */}
              {contest.eligibility && (
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                  <h3 className="text-lg font-semibold text-[#191919] mb-2">Eligibility</h3>
                  <p className="text-[#666666] text-sm">{contest.eligibility}</p>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              {/* Contest Info Card */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                <h3 className="font-semibold text-[#191919] mb-3">Contest Details</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-2.5">
                    <Calendar className="text-[#0a66c2]" size={16} />
                    <div>
                      <div className="text-xs text-[#666666]">Start Time</div>
                      <div className="text-[#191919] text-sm font-medium">
                        {new Date(contest.start_time).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <Clock className="text-[#0a66c2]" size={16} />
                    <div>
                      <div className="text-xs text-[#666666]">Duration</div>
                      <div className="text-[#191919] text-sm font-medium">{contest.duration_minutes} minutes</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <Users className="text-[#057642]" size={16} />
                    <div>
                      <div className="text-xs text-[#666666]">Participants</div>
                      <div className="text-[#191919] text-sm font-medium">{contest.participant_count || 0} registered</div>
                    </div>
                  </div>
                  {contest.mcqs?.length > 0 && (
                    <div className="flex items-center gap-2.5">
                      <CheckCircle className="text-[#0a66c2]" size={16} />
                      <div>
                        <div className="text-xs text-[#666666]">MCQs</div>
                        <div className="text-[#191919] text-sm font-medium">{contest.mcqs.length} questions</div>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2.5">
                    <Code className="text-[#915907]" size={16} />
                    <div>
                      <div className="text-xs text-[#666666]">Coding</div>
                      <div className="text-[#191919] text-sm font-medium">{contest.problems?.length || contest.problem_ids?.length || 0} problems</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* My Status */}
              {myStatus && (
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                  <h3 className="font-semibold text-[#191919] mb-3">My Performance</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center p-2.5 bg-[#f3f2ef] rounded-lg">
                      <div className="text-xl font-bold text-[#0a66c2]">{myStatus.total_score || 0}</div>
                      <div className="text-xs text-[#666666]">Score</div>
                    </div>
                    <div className="text-center p-2.5 bg-[#f3f2ef] rounded-lg">
                      <div className="text-xl font-bold text-[#915907]">#{myStatus.rank || '-'}</div>
                      <div className="text-xs text-[#666666]">Rank</div>
                    </div>
                    <div className="text-center p-2.5 bg-[#f3f2ef] rounded-lg">
                      <div className="text-xl font-bold text-[#057642]">{myStatus.problems_solved || 0}</div>
                      <div className="text-xs text-[#666666]">Solved</div>
                    </div>
                    <div className="text-center p-2.5 bg-[#f3f2ef] rounded-lg">
                      <div className="text-xl font-bold text-[#b24020]">{myStatus.submissions?.length || 0}</div>
                      <div className="text-xs text-[#666666]">Submissions</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Sponsors */}
              {contest.sponsors && contest.sponsors.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                  <h3 className="font-semibold text-[#191919] mb-3">Sponsors</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {contest.sponsors.map((sponsor, idx) => (
                      <span key={idx} className="px-2.5 py-1 bg-[#f3f2ef] text-[#666666] rounded-lg text-sm border border-gray-200">
                        {sponsor}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Problems Tab */}
        {activeTab === 'problems' && (
          <div className="space-y-4">
            {/* MCQs Section - Show First */}
            {contest.mcqs && contest.mcqs.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-[#191919] flex items-center gap-2">
                  <CheckCircle size={16} className="text-[#0a66c2]" />
                  MCQ Questions ({contest.mcqs.length})
                </h3>
                {contest.mcqs.map((mcq, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-white border border-gray-200 rounded-xl hover:border-[#0a66c2]/30 hover:shadow-md transition-all shadow-sm cursor-pointer"
                    data-testid={`mcq-${idx}`}
                    onClick={() => {/* Handle MCQ click - expand/collapse or navigate */}}
                  >
                    <div className="flex items-start gap-3">
                      <span className="w-9 h-9 bg-[#0a66c2]/10 rounded-lg flex items-center justify-center text-[#0a66c2] font-bold text-sm flex-shrink-0">
                        Q{idx + 1}
                      </span>
                      <div className="flex-1">
                        <div className="font-medium text-[#191919] text-sm mb-2">{mcq.question || mcq.title}</div>
                        {mcq.options && (
                          <div className="space-y-1.5">
                            {mcq.options.map((opt, optIdx) => (
                              <div key={optIdx} className="flex items-center gap-2 text-sm text-[#666666]">
                                <span className="w-5 h-5 border border-gray-300 rounded flex items-center justify-center text-xs">
                                  {String.fromCharCode(65 + optIdx)}
                                </span>
                                <span>{typeof opt === 'string' ? opt : opt.text}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="mt-2 flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-[#0a66c2]/10 text-[#0a66c2] rounded text-xs font-medium">
                            +{mcq.points || 10} points
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Coding Problems Section */}
            {contest.problems && contest.problems.length > 0 && (
              <div className="space-y-3">
                {contest.mcqs && contest.mcqs.length > 0 && (
                  <h3 className="text-sm font-semibold text-[#191919] flex items-center gap-2 mt-4">
                    <Code size={16} className="text-[#915907]" />
                    Coding Problems ({contest.problems.length})
                  </h3>
                )}
                {contest.problems.map((problem, idx) => (
                  <Link
                    key={idx}
                    to={`/codearena/problem/${problem.id}?contest=${contestId}`}
                    className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl hover:border-[#0a66c2]/30 hover:shadow-md transition-all shadow-sm"
                    data-testid={`problem-${idx}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-9 h-9 bg-[#e8f4f8] rounded-lg flex items-center justify-center text-[#0a66c2] font-bold text-sm">
                        {String.fromCharCode(65 + idx)}
                      </span>
                      <div>
                        <div className="font-medium text-[#191919] text-sm">{problem.title}</div>
                        <div className="text-xs text-[#666666]">+{problem.points} points</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        problem.difficulty === 'easy' ? 'bg-[#057642]/10 text-[#057642]' :
                        problem.difficulty === 'medium' ? 'bg-[#915907]/10 text-[#915907]' :
                        'bg-[#b24020]/10 text-[#b24020]'
                      }`}>
                        {problem.difficulty?.toUpperCase()}
                      </span>
                      <ChevronRight className="text-[#666666]" size={16} />
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* Empty State */}
            {(!contest.problems || contest.problems.length === 0) && (!contest.mcqs || contest.mcqs.length === 0) && (
              <div className="bg-white border border-gray-200 rounded-xl p-12 text-center shadow-sm">
                <Code className="mx-auto text-gray-300 mb-4" size={48} />
                <p className="text-[#666666] text-sm">
                  {isUpcoming ? 'Questions will be revealed when the contest starts' : 'No questions available'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            {leaderboard.length > 0 ? (
              <>
                <div className="grid grid-cols-12 gap-4 px-5 py-3 bg-[#f3f2ef] text-sm font-medium text-[#666666]">
                  <div className="col-span-1">Rank</div>
                  <div className="col-span-5">Participant</div>
                  <div className="col-span-3">College</div>
                  <div className="col-span-1 text-center">Solved</div>
                  <div className="col-span-2 text-right">Score</div>
                </div>
                <div className="divide-y divide-gray-100">
                  {leaderboard.map((entry, idx) => (
                    <div 
                      key={idx}
                      className={`grid grid-cols-12 gap-4 px-5 py-3 items-center hover:bg-gray-50 ${
                        entry.rank <= 3 ? 'bg-[#f3f2ef]/50' : ''
                      }`}
                    >
                      <div className="col-span-1">
                        {entry.rank === 1 ? (
                          <div className="w-8 h-8 bg-gradient-to-br from-[#915907] to-[#b87708] rounded-full flex items-center justify-center">
                            <Crown className="text-white" size={14} />
                          </div>
                        ) : entry.rank === 2 ? (
                          <div className="w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center">
                            <Medal className="text-white" size={12} />
                          </div>
                        ) : entry.rank === 3 ? (
                          <div className="w-8 h-8 bg-gradient-to-br from-[#b24020] to-[#d35030] rounded-full flex items-center justify-center">
                            <Medal className="text-white" size={12} />
                          </div>
                        ) : (
                          <span className="text-[#666666] font-semibold text-sm">{entry.rank}</span>
                        )}
                      </div>
                      <div className="col-span-5">
                        <Link 
                          to={entry.username ? `/codearena/profile/${entry.username}` : '#'}
                          className="font-medium text-[#191919] hover:text-[#0a66c2] text-sm"
                        >
                          {entry.name || 'Anonymous'}
                        </Link>
                      </div>
                      <div className="col-span-3 text-[#666666] truncate text-sm">{entry.college || '-'}</div>
                      <div className="col-span-1 text-center text-[#666666] text-sm">{entry.problems_solved || 0}</div>
                      <div className="col-span-2 text-right font-bold text-[#0a66c2]">{entry.total_score || 0}</div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="p-12 text-center">
                <Trophy className="mx-auto text-gray-300 mb-4" size={48} />
                <p className="text-[#666666] text-sm">No submissions yet</p>
              </div>
            )}
          </div>
        )}

        {/* My Submissions Tab */}
        {activeTab === 'my-submissions' && myStatus && (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            {myStatus.submissions && myStatus.submissions.length > 0 ? (
              <table className="w-full">
                <thead className="bg-[#f3f2ef]">
                  <tr>
                    <th className="px-5 py-3 text-left text-sm font-medium text-[#666666]">Problem</th>
                    <th className="px-5 py-3 text-center text-sm font-medium text-[#666666]">Language</th>
                    <th className="px-5 py-3 text-center text-sm font-medium text-[#666666]">Verdict</th>
                    <th className="px-5 py-3 text-center text-sm font-medium text-[#666666]">Score</th>
                    <th className="px-5 py-3 text-right text-sm font-medium text-[#666666]">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {myStatus.submissions.map((sub, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-5 py-3 text-[#191919] text-sm">{sub.problem_title || sub.problem_id}</td>
                      <td className="px-5 py-3 text-center text-[#666666] text-sm">{sub.language}</td>
                      <td className="px-5 py-3 text-center">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          sub.verdict === 'Accepted' 
                            ? 'bg-[#057642]/10 text-[#057642]' 
                            : 'bg-[#b24020]/10 text-[#b24020]'
                        }`}>
                          {sub.verdict}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-center text-[#0a66c2] font-semibold text-sm">+{sub.score_earned || 0}</td>
                      <td className="px-5 py-3 text-right text-[#666666] text-xs">
                        {new Date(sub.submitted_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-12 text-center">
                <Code className="mx-auto text-gray-300 mb-4" size={48} />
                <p className="text-[#666666] text-sm">No submissions yet. Start solving problems!</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CodeArenaContestsPage;
